import { cva } from 'class-variance-authority'
import { cn } from '~/lib/utils'
import { isDevelopment } from '~/utils/misc'

import Image from '~/components/Helpers/Image'
import BlockVideo from '~/components/Media/components/BlockVideo'
import Badge from '~/components/UI/General/Badge/Badge'
import StandardText from '~/components/Text/StandardText/StandardText'
import { FeatureBannerProps } from '~/components/types/banner'

const bannerVariants = cva('', {
  variants: {
    backgroundColour: {
      'light-blue': 'bg-blue-l text-blue-d',
      'light-pink': 'bg-pink text-blue-d',
      'light-aqua': 'bg-green text-blue-d',
      'light-orange': 'bg-orange text-blue-d',
      'light-purple': 'bg-purple text-blue-d',
      'light-grey': 'bg-grey-l text-blue-d',
      white: 'bg-white text-blue-d'
    },
    svgColour: {
      'light-blue': 'fill-blue-l',
      'light-pink': 'fill-pink',
      'light-aqua': 'fill-green',
      'light-orange': 'fill-orange',
      'light-purple': 'fill-purple',
      'light-grey': 'fill-grey-l',
      white: 'fill-white'
    }
  },
  defaultVariants: {
    backgroundColour: 'light-pink',
    svgColour: 'light-pink'
  }
})

const AvantFeatureBannerV2 = ({ content }: FeatureBannerProps) => {
  const {
    contained,
    reverse,
    image,
    video,
    badge,
    icon,
    contentBlock,
    buttons,
    backgroundColour,
    technicalTexture
  } = content

  const previewUrl =
    isDevelopment && !image?.asset.sys.uri.startsWith('https')
      ? 'https://preview-websitemodern-uni-hull.cloud.contensis.com'
      : ''

  return (
    <div
      className={cn(
        'flex flex-col xl:max-h-[680px]',
        reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'
      )}
    >
      <div
        className={cn(
          reverse ? 'justify-end' : 'justify-end lg:justify-start',
          contained
            ? 'aspect-4/3'
            : 'aspect-16/9 xl:aspect-auto xl:min-h-[680px]',
          'relative flex aspect-4/3 basis-[calc(50%+64px)] items-start lg:items-end'
        )}
      >
        <svg
          className={cn(
            bannerVariants({
              backgroundColour: null,
              svgColour: backgroundColour
            }),
            reverse ? '' : 'lg:-mr-[1px]',
            'size-s sm:size-l lg:size-xl absolute z-10 flex sm:items-end'
          )}
        >
          <rect x='0' className='size-full' />
        </svg>

        {video ? (
          <BlockVideo
            videoUrl={video.sys.uri}
            width={''}
            height={''}
            image={image}
            className='absolute'
            btnPosition={reverse ? 'bottom-left' : 'bottom-right'}
          />
        ) : (
          image && <Image image={image} classes='absolute inset-0' />
        )}

        {badge && badge.length > 0 && (
          <Badge
            className={cn(
              'sm:top-l left-xs-plus sm:left-l absolute top-0 -translate-y-1/2 sm:translate-0',
              reverse ? 'lg:right-l lg:left-auto' : ''
            )}
            data={badge[0]}
          />
        )}
      </div>
      <div
        className={cn(
          bannerVariants({
            backgroundColour: backgroundColour,
            svgColour: null
          }),
          reverse ? 'justify-end' : '',
          'relative flex basis-1/2 items-center'
        )}
      >
        <div
          className={cn(
            'lg:w-m bg-blue-d h-s sm:h-m absolute bottom-0 w-full mix-blend-hard-light lg:top-0 lg:h-full xl:[&>div]:h-full',
            reverse ? 'left-0' : 'right-0'
          )}
          style={{
            backgroundImage: `url(${previewUrl}${technicalTexture?.asset.sys.uri}?crop=32,800,center&q=75&f=webp)`,
            backgroundSize: 'cover',
            opacity: 1
          }}
        ></div>

        <div
          className={cn(
            'space-y-m px-xs-plus sm:p-l mb-s py-m lg:p-2xl flex h-full w-full max-w-[80ch] flex-col justify-center lg:mb-0',
            reverse ? 'lg:ml-m' : 'lg:mr-m'
          )}
        >
          <StandardText
            content={{
              ...contentBlock,
              icon: icon,
              buttons: buttons,
              ctaButtons: true
            }}
            backgroundColour={backgroundColour}
            className='[&_p]:max-w-[600px]'
          />
        </div>
      </div>
    </div>
  )
}

export default AvantFeatureBannerV2
